/**
 * Timeline Intelligence System
 * Connects Past (Sanctuary) → Present (Daily State) → Future (Future Self)
 */

import { supabase } from '@/integrations/supabase/client';

export type Timeline = 'past' | 'present' | 'future';

export interface TimelineConnection {
  id: string;
  user_id: string;
  past_pattern: string;
  past_source: string; // e.g., 'sanctuary', 'trauma_timeline'
  present_symptom: string;
  present_source: string; // e.g., 'daily_checkin', 'il_score'
  future_action: string;
  future_source: string; // e.g., 'future_self_step', 'identity_practice'
  confidence: number; // 0-100
  detected_at: Date;
}

export interface PastData {
  sanctuary_entries: any[];
  trauma_timeline: any[];
  recurring_patterns: any[];
  relationship_wounds: any[];
}

export interface PresentData {
  daily_checkin: any;
  il_score: any;
  mental_wellness: any;
  whoop_recovery: any;
  mood: string;
  energy: number;
  stress: number;
  cycle_day?: number;
  cycle_phase?: string;
}

export interface FutureData {
  future_self_identity: string;
  current_step: number;
  today_practice: string;
  intentions: string[];
  symbolic_anchors: string[];
  pivotal_moments: any[];
}

/**
 * Fetch all timeline data for a user
 */
export async function fetchTimelineData(userId: string): Promise<{
  past: PastData;
  present: PresentData;
  future: FutureData;
}> {
  // Fetch Past data - using balanced_guidance_sessions as Sanctuary equivalent
  const { data: sanctuaryData } = await supabase
    .from('balanced_guidance_sessions')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(10);

  // Extract patterns from questions and tags
  const patterns = sanctuaryData?.flatMap(s => s.tags || []).filter(Boolean) || [];

  // Fetch Present data
  const today = new Date().toISOString().split('T')[0];
  
  const { data: todayCheckin } = await supabase
    .from('daily_checkins')
    .select('*')
    .eq('user_id', userId)
    .eq('date', today)
    .single();

  const { data: ilScoreData } = await supabase
    .from('il_daily')
    .select('*')
    .eq('user_id', userId)
    .order('date', { ascending: false })
    .limit(1)
    .single();

  const { data: mentalWellnessData } = await supabase
    .from('mental_wellness_daily' as any)
    .select('*')
    .eq('user_id', userId)
    .order('date', { ascending: false })
    .limit(1)
    .single();

  const { data: whoopData } = await supabase
    .from('whoop_recovery_data')
    .select('*')
    .eq('user_id', userId)
    .order('recorded_at', { ascending: false })
    .limit(1)
    .single();

  // Fetch Future data - using daily_checkins for future self data
  const { data: futureCheckins } = await supabase
    .from('daily_checkins')
    .select('custom_future_self, daily_practicing, symbolic_anchor, future_self_version')
    .eq('user_id', userId)
    .not('custom_future_self', 'is', null)
    .order('date', { ascending: false })
    .limit(1);

  const latestFutureSelf = futureCheckins?.[0];

  return {
    past: {
      sanctuary_entries: sanctuaryData || [],
      trauma_timeline: [],
      recurring_patterns: [...new Set(patterns)],
      relationship_wounds: []
    },
    present: {
      daily_checkin: todayCheckin,
      il_score: ilScoreData,
      mental_wellness: mentalWellnessData,
      whoop_recovery: whoopData,
      mood: todayCheckin?.daily_feeling || 'unknown',
      energy: todayCheckin?.energy_field ? 5 : 3,
      stress: ilScoreData?.stress_score || 5,
      cycle_day: todayCheckin?.menstrual_cycle_day,
      cycle_phase: todayCheckin?.menstrual_flow
    },
    future: {
      future_self_identity: latestFutureSelf?.custom_future_self || todayCheckin?.custom_future_self || '',
      current_step: 1,
      today_practice: todayCheckin?.daily_practicing || '',
      intentions: [],
      symbolic_anchors: todayCheckin?.symbolic_anchor ? [todayCheckin.symbolic_anchor] : [],
      pivotal_moments: []
    }
  };
}

/**
 * Detect connections between timelines
 * Example: Past trauma pattern → Present stress spike → Future identity work
 */
export function detectTimelineConnections(
  past: PastData,
  present: PresentData,
  future: FutureData
): TimelineConnection[] {
  const connections: TimelineConnection[] = [];

  // Pattern 1: Sanctuary pattern → IL/Stress → Future Self reframe
  if (past.recurring_patterns.length > 0 && present.il_score) {
    past.recurring_patterns.forEach((pattern: string) => {
      if (present.il_score.stress_score > 7) {
        connections.push({
          id: `${Date.now()}-stress-pattern`,
          user_id: '',
          past_pattern: pattern,
          past_source: 'sanctuary',
          present_symptom: 'High stress detected',
          present_source: 'il_score',
          future_action: future.future_self_identity 
            ? `Your Future Self would approach this differently: "${future.future_self_identity}"`
            : 'Consider setting an intention for how you want to show up',
          future_source: 'future_self_identity',
          confidence: 75,
          detected_at: new Date()
        });
      }
    });
  }

  // Pattern 2: Relationship wounds → Mood drop → Identity practice
  if (past.sanctuary_entries.some(e => e.experience_type === 'relationship')) {
    if (present.mood === 'struggling' || present.mood === 'crisis') {
      connections.push({
        id: `${Date.now()}-mood-wound`,
        user_id: '',
        past_pattern: 'Relationship wound pattern',
        past_source: 'sanctuary',
        present_symptom: `Mood: ${present.mood}`,
        present_source: 'daily_checkin',
        future_action: future.today_practice || 'Create a grounding practice',
        future_source: 'today_practice',
        confidence: 70,
        detected_at: new Date()
      });
    }
  }

  // Pattern 3: Cycle + Emotion + Past pattern
  if (present.cycle_day && present.cycle_day > 20) {
    const lutealMood = ['struggling', 'crisis'].includes(present.mood);
    if (lutealMood && past.sanctuary_entries.length > 0) {
      connections.push({
        id: `${Date.now()}-cycle-pattern`,
        user_id: '',
        past_pattern: 'Past emotional pattern',
        past_source: 'sanctuary',
        present_symptom: `Luteal phase + low mood (Day ${present.cycle_day})`,
        present_source: 'cycle_tracking',
        future_action: 'Honor this phase with gentle self-care',
        future_source: 'future_self_guidance',
        confidence: 80,
        detected_at: new Date()
      });
    }
  }

  // Pattern 4: WHOOP recovery + Emotional sensitivity + Future Self
  if (present.whoop_recovery && present.whoop_recovery.recovery_score < 50) {
    if (present.energy < 5) {
      connections.push({
        id: `${Date.now()}-recovery-energy`,
        user_id: '',
        past_pattern: 'Low recovery affects emotional capacity',
        past_source: 'whoop_data',
        present_symptom: `Recovery ${present.whoop_recovery.recovery_score}%, Energy low`,
        present_source: 'whoop_recovery',
        future_action: future.future_self_identity 
          ? `${future.future_self_identity} would prioritise rest today`
          : 'Today is a day for gentleness and restoration',
        future_source: 'future_self_identity',
        confidence: 85,
        detected_at: new Date()
      });
    }
  }

  return connections;
}

/**
 * Generate timeline-aware insight
 */
export function generateTimelineInsight(
  timelineData: { past: PastData; present: PresentData; future: FutureData }
): string {
  const connections = detectTimelineConnections(
    timelineData.past,
    timelineData.present,
    timelineData.future
  );

  if (connections.length === 0) {
    return `You're on track. Keep building your ${timelineData.future.future_self_identity || 'best self'}.`;
  }

  // Return the highest confidence connection
  const topConnection = connections.sort((a, b) => b.confidence - a.confidence)[0];

  return `💡 ${topConnection.present_symptom} may connect to ${topConnection.past_pattern}. ${topConnection.future_action}`;
}

/**
 * Get timeline position
 */
export function getTimelinePosition(futureData: FutureData): {
  step: number;
  total_steps: number;
  progress_percentage: number;
} {
  const totalSteps = futureData.pivotal_moments.length || 10;
  const currentStep = futureData.current_step || 1;
  
  return {
    step: currentStep,
    total_steps: totalSteps,
    progress_percentage: Math.round((currentStep / totalSteps) * 100)
  };
}

/**
 * Create a timeline narrative for AI context
 */
export function buildTimelineNarrative(
  past: PastData,
  present: PresentData,
  future: FutureData
): string {
  const narrative: string[] = [];

  // Past
  if (past.sanctuary_entries.length > 0) {
    narrative.push(`Past: User has logged ${past.sanctuary_entries.length} Sanctuary experiences.`);
    if (past.recurring_patterns.length > 0) {
      narrative.push(`Recurring patterns: ${past.recurring_patterns.slice(0, 3).join(', ')}`);
    }
  }

  // Present
  narrative.push(`Present: Mood is "${present.mood}", Energy ${present.energy}/10.`);
  if (present.il_score) {
    narrative.push(`IL Score: ${present.il_score.il_score}/100 (${present.il_score.bucket})`);
  }
  if (present.whoop_recovery) {
    narrative.push(`Recovery: ${present.whoop_recovery.recovery_score}%`);
  }
  if (present.cycle_day) {
    narrative.push(`Cycle Day ${present.cycle_day}`);
  }

  // Future
  if (future.future_self_identity) {
    narrative.push(`Future Self: "${future.future_self_identity}"`);
  }
  if (future.today_practice) {
    narrative.push(`Today's Practice: ${future.today_practice}`);
  }

  return narrative.join(' | ');
}
